import { compare, multiplyByRatio, subtract, type Money } from "@stb/core/money"

import { calculateCentsPerKm, centsPerKmRate, type CentsPerKmRate } from "./cents-per-km"

export interface LogbookVsCentsPerKmInput {
  financialYear: string
  /** Work-related km in one car for the income year. */
  businessKm: number
  /** Business-use share from the logbook in basis points (6200 = 62%); 1..10000. */
  businessShareBp: number
  /** Total car running costs for the year (AUD). */
  carCosts: Money
}

export type LogbookVsCentsPerKmResult =
  | {
      better: "logbook" | "cents-per-km" | "equal"
      centsPerKm: Money
      logbook: Money
      /** Absolute gap between the two amounts. */
      difference: Money
      /** Yearly car costs above which the logbook method pays more (cents-per-km amount / business share). */
      breakEvenCosts: Money
      rate: CentsPerKmRate
    }
  | {
      /** Over the 5,000 km cap only the logbook amount is shown; no verdict is given. */
      better: null
      centsPerKm: null
      logbook: Money
      difference: null
      breakEvenCosts: null
      rate: CentsPerKmRate
    }

/**
 * Compares the cents per km and logbook methods (R4.22). Logbook = costs x share; break-even costs =
 * cents-per-km amount / share. Both round half-up to the cent. Estimate only: not tax advice.
 */
export function compareLogbookVsCentsPerKm(
  input: LogbookVsCentsPerKmInput,
): LogbookVsCentsPerKmResult {
  const { businessShareBp, carCosts } = input
  if (!Number.isInteger(businessShareBp) || businessShareBp < 1 || businessShareBp > 10000) {
    throw new RangeError(
      `businessShareBp must be a whole number from 1 to 10000, got ${businessShareBp}`,
    )
  }
  if (carCosts.currency !== "AUD" || carCosts.amount < 0) {
    throw new RangeError("carCosts must be a non-negative AUD amount")
  }
  const logbook = multiplyByRatio(carCosts, businessShareBp, 10000)
  const cents = calculateCentsPerKm(input.financialYear, input.businessKm)
  if (!cents.ok) {
    const rate = centsPerKmRate(input.financialYear)
    return {
      better: null,
      centsPerKm: null,
      logbook,
      difference: null,
      breakEvenCosts: null,
      rate,
    }
  }
  const order = compare(logbook, cents.deduction)
  return {
    better: order > 0 ? "logbook" : order < 0 ? "cents-per-km" : "equal",
    centsPerKm: cents.deduction,
    logbook,
    difference:
      order >= 0 ? subtract(logbook, cents.deduction) : subtract(cents.deduction, logbook),
    breakEvenCosts: multiplyByRatio(cents.deduction, 10000, businessShareBp),
    rate: cents.rate,
  }
}
